import { CloseIcon } from '@chakra-ui/icons';
import { Flex, IconButton, Select, useToast } from '@chakra-ui/react';
import { Button, Divider, HStack, Tag, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAddUserToClientMutation,
  useGetUserQuery,
  useListClientsQuery,
  useRemoveUserFromClientMutation,
} from '@/@generated/codegen/API';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';

import { AdminUserListItemType } from '../AdminUserList/columns';

type Props = {
  row: AdminUserListItemType;
};

export default function ManageUserClientsView({ row }: Props) {
  const { t } = useTranslation();
  const { apolloClient } = useApolloClientContext();
  const allClients = useListClientsQuery();
  const user = useGetUserQuery({
    variables: {
      where: {
        id: row.id,
      },
    },
  });
  const clientsUserIsPartOf = useMemo(() => {
    if (!allClients.data || !user.data) return [];
    return allClients.data?.listClients.edges
      .map((el) => el.node)
      .filter((client) => client.id === user.data?.getUser?.clientId);
  }, [allClients.data, user.data]);
  const clientsUserIsNOTPartOf = useMemo(() => {
    if (!allClients.data) return [];
    return allClients.data?.listClients.edges
      .map((el) => el.node)
      .filter((client) => {
        return !clientsUserIsPartOf?.some((c) => c.id === client.id) && client.id !== row.clientId;
      });
  }, [allClients.data, clientsUserIsPartOf]);

  const [addUserToClient] = useAddUserToClientMutation();
  const [removeUserFromClient] = useRemoveUserFromClientMutation();
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const toast = useToast();

  const handleAddToClient = () => {
    if (!selectedClientId) {
      toast({
        description: t('admin.manageUserView.clients.error.invalid'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    addUserToClient({
      variables: {
        data: {
          userId: row.id,
          clientId: selectedClientId,
        },
      },
    })
      .then(() => {
        void user.refetch();
        void apolloClient.refetchQueries({
          include: ['AdminUserListView'],
        });
        toast({
          description: t('admin.manageUserView.clients.success.added'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      })
      .catch((error: Error) => {
        console.error('addUserToClient error: ', error);
        toast({
          description: t('admin.manageUserView.clients.error.added'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };

  const handleRemoveFromClient = (clientId: string) => {
    removeUserFromClient({
      variables: {
        data: {
          userId: row.id,
          clientId: clientId,
        },
      },
    })
      .then(() => {
        void user.refetch();
        void apolloClient.refetchQueries({
          include: ['AdminUserListView'],
        });
        toast({
          description: t('admin.manageUserView.clients.success.removed'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      })
      .catch(() => {
        toast({
          description: t('admin.manageUserView.clients.error.removed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };

  return (
    <>
      <Text as="b">Clients:</Text>
      <Divider marginY="10px" />
      {clientsUserIsPartOf.length === 0 ? (
        <Text>No clients</Text>
      ) : (
        <Flex flexWrap="wrap" gap="10px">
          {clientsUserIsPartOf.map((client) => (
            <Tag size="lg" key={client.id} variant="solid">
              {client.name}
              <IconButton
                aria-label="delete-client-user"
                icon={<CloseIcon />}
                size="xs"
                style={{ background: 'transparent' }}
                onClick={() => handleRemoveFromClient(client.id)}
              />
            </Tag>
          ))}
        </Flex>
      )}
      <Divider marginY="10px" />
      {clientsUserIsNOTPartOf && (
        <HStack spacing="5px" justifyContent="space-between" w="full">
          <Select
            flex="1 0 100px"
            placeholder={clientsUserIsNOTPartOf.length === 0 ? 'No clients' : 'Select client'}
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
          >
            {clientsUserIsNOTPartOf.map((client) => {
              return (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              );
            })}
          </Select>
          <Button variant="secondary" title="Add-Client" onClick={() => handleAddToClient()}>
            {t('admin.manageUserView.button.addClient')}
          </Button>
        </HStack>
      )}
    </>
  );
}
